import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Scroll-triggered reveal for the "What You Can Identify" section:
 * label, title, subtitle, then bento grid cards (staggered).
 */
export class IdentifySectionReveal {
  private section: HTMLElement | null = null;
  private label: HTMLElement | null = null;
  private title: HTMLElement | null = null;
  private subtitle: HTMLElement | null = null;
  private howStrip: HTMLElement | null = null;
  private stats: HTMLElement | null = null;
  private cards: HTMLElement[] = [];
  private timeline: gsap.core.Timeline | null = null;

  constructor() {
    this.section = document.querySelector('.identify-section');
    if (!this.section) return;

    this.label = this.section.querySelector('.identify-section-label');
    this.title = this.section.querySelector('.identify-section-title');
    this.subtitle = this.section.querySelector('.identify-section-subtitle');
    this.howStrip = this.section.querySelector('.identify-how-strip');
    this.stats = this.section.querySelector('.identify-stats');
    const grid = this.section.querySelector('.identify-grid');
    this.cards = grid ? Array.from(grid.querySelectorAll('.identify-card')) : [];

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      gsap.set(
        [this.label, this.title, this.subtitle, this.howStrip, this.stats].filter(Boolean),
        { opacity: 1, y: 0, scale: 1 }
      );
      gsap.set(this.cards, { opacity: 1, y: 0 });
      return;
    }

    this.setInitialStates();
    this.setupScrollReveal();
  }

  private setInitialStates(): void {
    if (this.label) gsap.set(this.label, { opacity: 0, y: -16 });
    if (this.title) gsap.set(this.title, { opacity: 0, y: 28, scale: 0.94 });
    if (this.subtitle) gsap.set(this.subtitle, { opacity: 0, y: 20 });
    if (this.howStrip) gsap.set(this.howStrip, { opacity: 0, y: 24 });
    if (this.stats) gsap.set(this.stats, { opacity: 0, y: 16 });
    this.cards.forEach((card) => gsap.set(card, { opacity: 0, y: 48 }));
  }

  private setupScrollReveal(): void {
    if (!this.section) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.section,
        start: 'top 88%',
        end: 'top 25%',
        scrub: 1.2,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (self.progress === 1) {
            this.cards.forEach((card) => gsap.set(card, { clearProps: 'transform' }));
          }
        },
      },
      defaults: { ease: 'power3.out' },
    });

    if (this.label) tl.to(this.label, { opacity: 1, y: 0, duration: 0.6 }, 0);
    if (this.title) {
      tl.to(this.title, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.85,
        ease: 'back.out(1.2)',
      }, 0.15);
    }
    if (this.subtitle) tl.to(this.subtitle, { opacity: 1, y: 0, duration: 0.65 }, 0.35);
    if (this.howStrip) tl.to(this.howStrip, { opacity: 1, y: 0, duration: 0.5 }, 0.4);
    if (this.stats) tl.to(this.stats, { opacity: 1, y: 0, duration: 0.5 }, 0.45);
    if (this.cards.length) {
      tl.to(
        this.cards,
        {
          opacity: 1,
          y: 0,
          duration: 0.75,
          stagger: 0.09,
          ease: 'power3.out',
        },
        0.55
      );
    }

    this.timeline = tl;
  }

  dispose(): void {
    this.timeline?.scrollTrigger?.kill();
    this.timeline?.kill();
    this.timeline = null;
    this.section = null;
    this.label = null;
    this.title = null;
    this.subtitle = null;
    this.howStrip = null;
    this.stats = null;
    this.cards = [];
  }
}
